"use client";

import type * as React from "react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

import { cn } from "@/lib/utils";
import { useSidebar } from "./context";
import { useTranslation } from "react-i18next";

export function Sidebar({
	side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  side?: "left" | "right";
  variant?: "sidebar" | "floating" | "inset";
  collapsible?: "offcanvas" | "icon" | "none";
}) {
  const {
    isMobile,
    state,
    openMobile,
    setOpenMobile,
    SIDEBAR_WIDTH,
    SIDEBAR_WIDTH_MOBILE,
    SIDEBAR_WIDTH_ICON,
    } = useSidebar();
    const { t } = useTranslation();

    if (collapsible === "none") {
    	return (
    		<div
    			data-slot="sidebar"
    			className={cn(
    				"bg-sidebar text-sidebar-foreground flex h-full w-[var(--sidebar-width)] flex-col",
    				className,
    			)}
    			style={
    				{
    					"--sidebar-width": SIDEBAR_WIDTH,
    				} as React.CSSProperties
    			}
    			{...props}
    		>
    			{children}
    		</div>
    	);
    }

    if (isMobile) {
    	return (
    		<Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
    			<SheetContent
    				data-sidebar="sidebar"
    				data-slot="sidebar"
    				data-mobile="true"
    				className="bg-sidebar text-sidebar-foreground w-[var(--sidebar-width)] p-0 [&>button]:hidden"
    				style={
    					{
    						"--sidebar-width": SIDEBAR_WIDTH_MOBILE,
    					} as React.CSSProperties
    				}
    				side={side}
    			>
    				<SheetHeader className="sr-only">
    					<SheetTitle>{t('sidebar.title')}</SheetTitle>
    					<SheetDescription>{t('sidebar.description')}</SheetDescription>
    				</SheetHeader>
    				<div className="flex h-full w-full flex-col">{children}</div>
    			</SheetContent>
    		</Sheet>
    	);
    }

    return (
    <div
      className="group peer text-sidebar-foreground hidden md:block"
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
      style={
        {
          "--sidebar-width": SIDEBAR_WIDTH,
          "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
        } as React.CSSProperties
      }
    >
      <div
        data-slot="sidebar-gap"
        className={cn(
          "relative w-[var(--sidebar-width)] bg-transparent transition-[width] duration-200 ease-linear",
          "group-data-[collapsible=offcanvas]:w-0",
          "group-data-[side=right]:rotate-180",
          variant === "floating" || variant === "inset"
            ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+theme(spacing.4))]"
            : "group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)]",
        )}
      />
      <div
        data-slot="sidebar-container"
        className={cn(
          "fixed inset-y-0 z-10 hidden h-svh w-[var(--sidebar-width)] transition-[left,right,width] duration-200 ease-linear md:flex",
          side === "left"
            ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
            : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
          variant === "floating" || variant === "inset"
            ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+theme(spacing.4)+2px)]"
            : "group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)] group-data-[side=left]:border-r group-data-[side=right]:border-l",
          className,
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className="bg-sidebar group-data-[variant=floating]:border-sidebar-border flex h-full w-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow-sm"
        >
          {children}
        </div>
      </div>
    </div>
  );
}
